/**
 * Anaka error-surface scenario catalogue.
 *
 * One parametric surface ("ErrorSurface") is reused wherever a page can't be
 * reached; this module holds the *content* for each scenario in Anaka's
 * calm & reassuring voice. Copy is lifted verbatim from the Claude Design
 * handoff (`404 Page.html` / `screens/not-found.jsx`).
 *
 * Final design decisions captured from the design chat (chat25):
 *   • no eyebrow "scenario tag"            → not modelled here
 *   • no mono "error reference" footer     → not modelled here
 *   • the status banner has no "live status" link (no health page yet)
 *
 * Curly apostrophes (’) are intentional — they match the authored copy.
 */

export type ErrorScenarioId =
  | 'mistyped'
  | 'moved'
  | 'forbidden'
  | 'expired'
  | 'unauthenticated'
  | 'degraded'
  | 'outage'
  | 'serverError';

/** Which static action set the surface offers. */
export type ErrorActionSet = 'backHome' | 'login';

export type ErrorBannerLevel = 'degraded' | 'down';

export interface ErrorBanner {
  level: ErrorBannerLevel;
  text: string;
}

export interface ErrorScenario {
  id: ErrorScenarioId;
  /** HTTP status this scenario represents. */
  httpStatus: number;
  /** Numeral shown in the 4·A·4 motif and the document title (motif uses first+last char). */
  code: string;
  /** The human one-liner. */
  headline: string;
  /** Anaka's plain-language explanation. */
  message: string;
  /** Default action set (Back/Home, or a single "Go to login"). */
  actions: ErrorActionSet;
  /** System-status strip beneath the nav — only for the not-operational scenarios. */
  banner?: ErrorBanner;
  /**
   * Forces the top chrome regardless of auth. The unauthenticated surface is
   * always the minimal wordmark (there's no session to show a nav for); every
   * other scenario lets the page choose nav (signed in) vs wordmark (guest).
   */
  forceTopbar?: 'wordmark';
  /** Glyph treatment in the motif. */
  motif?: 'standard' | 'broken';
}

export const ERROR_SCENARIOS: Record<ErrorScenarioId, ErrorScenario> = {
  // ── 404 · mistyped or broken URL ──────────────────────────────────────────
  mistyped: {
    id: 'mistyped',
    httpStatus: 404,
    code: '404',
    headline: 'Let’s get you back on track.',
    message:
      'I couldn’t find a page at this address — it may have moved, or the link had a small typo. Nothing’s broken on your end. Head back or go home and I’ll help you find what you were after.',
    actions: 'backHome',
    motif: 'broken',
  },
  // ── 404 · page moved or archived ──────────────────────────────────────────
  moved: {
    id: 'moved',
    httpStatus: 404,
    code: '404',
    headline: 'That page has moved on.',
    message:
      'The page that used to live here was archived or relocated. Your work is safe and still in your workspace — let’s get you somewhere familiar and pick things up from there.',
    actions: 'backHome',
  },
  // ── 403 · no access ───────────────────────────────────────────────────────
  forbidden: {
    id: 'forbidden',
    httpStatus: 403,
    code: '403',
    headline: 'This one’s behind a closed door.',
    message:
      'This page exists — it’s just part of a workspace you’re not a member of right now, or your access was recently changed. If that seems off, your workspace admin can restore it for you.',
    actions: 'backHome',
  },
  // ── 410 · expired share link ──────────────────────────────────────────────
  expired: {
    id: 'expired',
    httpStatus: 410,
    code: '410',
    headline: 'This share link has expired.',
    message:
      'The link you followed had an expiry date, and it’s now passed — so the workspace behind it is closed to guests again. Nothing’s lost. The owner can send a fresh link whenever you need it.',
    actions: 'backHome',
  },
  // ── 401 · unauthenticated visitor ─────────────────────────────────────────
  unauthenticated: {
    id: 'unauthenticated',
    httpStatus: 401,
    code: '401',
    headline: 'Please sign in to continue.',
    message:
      'You’ve reached part of Anaka Cloud that lives behind a sign-in, so there’s nothing here I can safely show you yet. Sign in and I’ll take you straight to where you were heading.',
    actions: 'login',
    forceTopbar: 'wordmark',
  },
  // ── 503 · degraded service ────────────────────────────────────────────────
  degraded: {
    id: 'degraded',
    httpStatus: 503,
    code: '503',
    headline: 'We’re working on a hiccup.',
    message:
      'A few of our services are running slowly at the moment, which is why this page couldn’t load fully. This is on us, not you — your work is safe. Give it a minute and try again, or head home while we sort it out.',
    actions: 'backHome',
    banner: {
      level: 'degraded',
      text: 'Some Anaka Cloud services are degraded right now — you may see slower or partial data.',
    },
  },
  // ── 503 · major outage ────────────────────────────────────────────────────
  outage: {
    id: 'outage',
    httpStatus: 503,
    code: '503',
    headline: 'We’re temporarily offline.',
    message:
      'We’re in the middle of an outage and most of Anaka Cloud is unreachable right now. Nothing you’ve done caused this, and your work is safe. We’re actively restoring service and will be back shortly.',
    actions: 'backHome',
    banner: {
      level: 'down',
      text: 'Anaka Cloud is experiencing a major outage. Our team is on it.',
    },
  },
  // ── 500 · generic server error (fallback, same calm voice) ────────────────
  serverError: {
    id: 'serverError',
    httpStatus: 500,
    code: '500',
    headline: 'Something went wrong on our end.',
    message:
      'An unexpected error stopped this page from loading. It’s on us, not you — your work is safe. Give it another try, and if it keeps happening our team can take a look.',
    actions: 'backHome',
  },
};

/** Canonical scenario for a given HTTP status (used by the `/error/:code` route). */
const STATUS_TO_SCENARIO: Record<number, ErrorScenarioId> = {
  401: 'unauthenticated',
  403: 'forbidden',
  404: 'mistyped',
  410: 'expired',
  500: 'serverError',
  503: 'degraded',
};

/**
 * Resolve the scenario for an HTTP status (number or string route param).
 * Unknown codes fall back by class: 5xx → server error, anything else →
 * "page not found", which is also the fallback for unparseable input.
 */
export function resolveScenarioByStatus(status: number | string | undefined): ErrorScenario {
  const code = typeof status === 'string' ? Number.parseInt(status, 10) : status;

  if (typeof code === 'number' && Number.isFinite(code)) {
    const exact = STATUS_TO_SCENARIO[code];
    if (exact) return ERROR_SCENARIOS[exact];
    if (code >= 500 && code < 600) return ERROR_SCENARIOS.serverError;
  }

  return ERROR_SCENARIOS.mistyped;
}
